import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { HistoryButton } from '@/features/audit-log';
import { ApiError } from '@/shared/api/api-error';
import { ErrorState } from '@/shared/components/error-state';
import { OrderReason } from '@/shared/components/order-reason';
import { SortableOrderList } from '@/shared/components/sortable-order-list';
import { StatusBadge } from '@/shared/components/status-badge';

import { usePaymentChannels, useSaveChannelOrder } from '../api/pricing.queries';
import type { PaymentChannel } from '../types/pricing.types';

/**
 * A country's checkout order (pricing.config.manage): the order of the payment
 * methods customers choose from. The same editor as the brand, category and
 * region orders: drag a row by its handle (or with the keyboard), or use its
 * menu; give a reason; save.
 *
 * Channels that are switched off are listed too — they keep their place if
 * switched back on. Saving sends every id in the new order; the backend
 * validates, numbers them 1..n and audits it. Mount only while open.
 */
export function ChannelOrderDialog({
  countryId,
  countryName,
  onClose,
}: {
  countryId: number;
  countryName: string;
  onClose: () => void;
}) {
  const channels = usePaymentChannels(countryId);
  // Owned here, not by the editor, so a refusal stays on screen after the
  // stale list is reloaded and the editor starts afresh.
  const save = useSaveChannelOrder(countryId);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-3 sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-1">
            <DialogTitle className="text-base">Checkout order — {countryName}</DialogTitle>
            <HistoryButton
              entityType="PaymentChannelOrder"
              entityId={String(countryId)}
              name={`the ${countryName} checkout order`}
            />
          </div>
          <DialogDescription className="text-xs">
            The order of the payment methods customers in {countryName} choose from at checkout.
            Channels that are off are not shown to customers.
          </DialogDescription>
        </DialogHeader>
        {channels.isLoading ? (
          <Skeleton className="h-48" />
        ) : channels.error || !channels.data ? (
          <ErrorState error={channels.error} onRetry={() => void channels.refetch()} />
        ) : (
          <OrderEditor
            // A refetch (e.g. after a refused, stale save) starts the editor afresh.
            key={channels.dataUpdatedAt}
            initial={channels.data}
            save={save}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function OrderEditor({
  initial,
  save,
  onClose,
}: {
  initial: PaymentChannel[];
  save: ReturnType<typeof useSaveChannelOrder>;
  onClose: () => void;
}) {
  const [order, setOrder] = useState(initial);
  const [reason, setReason] = useState('');
  const originalIndex = new Map(initial.map((channel, index) => [channel.id, index]));
  const moved = order.filter((c, index) => originalIndex.get(c.id) !== index).length;
  // Legacy data may hold clashing or gapped positions; saving the order as shown fixes them.
  const needsNumbering = initial.some((channel, index) => channel.sortOrder !== index + 1);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 text-xs">
      {needsNumbering && (
        <p role="status" className="text-status-warning">
          Some channels share a position or have gaps. Saving numbers them 1 to {initial.length} in
          the order shown.
        </p>
      )}
      <SortableOrderList
        className="min-h-0 flex-1"
        listId="channel-order"
        label="Checkout order"
        items={order}
        onChange={setOrder}
        originalIndex={originalIndex}
        itemLabel={(channel) => channel.name}
        disabled={save.isPending}
        renderItem={(channel) => (
          <>
            <span className="truncate font-medium">{channel.name}</span>
            <code className="text-muted-foreground font-mono text-[11px]">{channel.code}</code>
            {!channel.isActive && <StatusBadge variant="neutral">Off</StatusBadge>}
          </>
        )}
      />
      <OrderReason id="channel-order-reason" value={reason} onChange={setReason} />
      {save.error instanceof ApiError && (
        <p role="alert" className="text-destructive">
          {save.error.message}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <span className="text-muted-foreground me-auto" aria-live="polite">
          {moved === 0
            ? needsNumbering
              ? 'No moves — saving renumbers them.'
              : 'No changes yet.'
            : `${moved} channel${moved === 1 ? '' : 's'} moved.`}
        </span>
        <Button type="button" size="sm" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={(moved === 0 && !needsNumbering) || reason.trim().length < 3 || save.isPending}
          onClick={() =>
            save.mutate(
              { channelIds: order.map((c) => c.id), reason: reason.trim() },
              { onSuccess: onClose },
            )
          }
        >
          Save order
        </Button>
      </div>
    </div>
  );
}
